import { useState, useEffect, useCallback } from 'react'
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Autocomplete,
  TextField,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { locationService, storeLocationMappingService } from '../services'
import useAuthStore from '../store/authStore'
import { hasAdminPermission, PERMISSION_SUBJECTS } from '../utils/adminPermissions'

const SEARCH_DEBOUNCE_MS = 450

function useDebouncedValue(value, ms) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

const DefaultOrgLocationSettingsPage = () => {
  const adminPermissions = useAuthStore((s) => s.adminPermissions)
  const canWrite = hasAdminPermission(
    adminPermissions,
    PERMISSION_SUBJECTS.DEFAULT_ORG_LOCATION,
    'write',
  )
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState(null)
  const [locInput, setLocInput] = useState('')
  const debouncedLocInput = useDebouncedValue(locInput, SEARCH_DEBOUNCE_MS)
  const [locOptions, setLocOptions] = useState([])
  const [locLoading, setLocLoading] = useState(false)

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await storeLocationMappingService.getDefaultOrgLocationSettings()
      const loc = data?.location
      if (loc?.id) {
        setSelectedLocation({ id: loc.id, name: loc.name, code: loc.code })
        setLocInput(`${loc.code} — ${loc.name}`)
      } else if (data?.default_location_code) {
        setSelectedLocation(null)
        setLocInput(data.default_location_code)
      } else {
        setSelectedLocation(null)
        setLocInput('')
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load default location settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLocLoading(true)
        const q = debouncedLocInput.trim()
        const data = await locationService.getLocations({
          page: 1,
          limit: 50,
          ...(q ? { search: q } : {}),
        })
        if (cancelled) return
        setLocOptions(data?.items || [])
      } catch {
        if (!cancelled) setLocOptions([])
      } finally {
        if (!cancelled) setLocLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [debouncedLocInput])

  const handleSave = async () => {
    if (!canWrite) return
    if (!selectedLocation?.code) {
      setError('Select a warehouse location')
      return
    }
    try {
      setSaving(true)
      setError(null)
      const data = await storeLocationMappingService.updateDefaultOrgLocationSettings(
        selectedLocation.code,
      )
      const loc = data?.location
      if (loc?.id) {
        setSelectedLocation({ id: loc.id, name: loc.name, code: loc.code })
        setLocInput(`${loc.code} — ${loc.name}`)
      }
      setSuccessMessage('Default shop location saved')
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        err.message ||
        'Failed to save default location settings'
      setError(Array.isArray(msg) ? msg.join(', ') : String(msg))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <LocationOnIcon sx={{ color: '#e90000', fontSize: 32 }} />
        <Typography variant="h5" fontWeight={600}>
          Default shop location
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Warehouse location for new shops
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            New shops are automatically linked to this warehouse location.
            Changes apply only to shops created after saving.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
              sx={{ maxWidth: 480 }}
            >
              <Autocomplete
                options={locOptions}
                value={selectedLocation}
                onChange={(_, value) => setSelectedLocation(value)}
                inputValue={locInput}
                onInputChange={(_, value) => setLocInput(value)}
                getOptionLabel={(opt) =>
                  opt?.code ? `${opt.code} — ${opt.name || ''}` : ''
                }
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                loading={locLoading}
                disabled={saving || !canWrite}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Default warehouse location"
                    placeholder="Search by code or name"
                    helperText="Select the location code to assign when a new shop is created"
                  />
                )}
              />
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={
                    saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
                  }
                  disabled={saving || !canWrite}
                  sx={{
                    bgcolor: '#e90000',
                    '&:hover': { bgcolor: '#c40000' },
                  }}
                >
                  Save
                </Button>
                <Button variant="outlined" onClick={loadSettings} disabled={loading || saving}>
                  Reset
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Container>
  )
}

export default DefaultOrgLocationSettingsPage
